import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Steps, Step, PropTable, Code, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'overview', title: 'Overview', level: 2 as const },
  { id: 'add', title: 'Adding a mailbox', level: 2 as const },
  { id: 'fields', title: 'Mailbox fields', level: 2 as const },
  { id: 'how-it-works', title: 'How it works', level: 2 as const },
]

export function Mailboxes() {
  return (
    <Article toc={toc}>
      <H1>Mailboxes</H1>
      <Lead>Connect any IMAP mailbox to Rabbit. When a new email arrives, Rabbit automatically processes it and emits a <Code>email.received</Code> BUZZ event.</Lead>

      <H2 id="overview">Overview</H2>
      <P>The Mail Service polls each configured mailbox at a regular interval. When new emails are found, their metadata and attachments are stored and a BUZZ event is published — allowing Brainer workflows or external systems to react.</P>

      <H2 id="add">Adding a mailbox</H2>
      <Steps>
        <Step n={1} title="Open Mail › Mailboxes">Click <strong>New Mailbox</strong> in the top-right corner.</Step>
        <Step n={2} title="Enter IMAP credentials">
          Provide the host, port, TLS setting, email address, and password. For Gmail, use <Code>imap.gmail.com:993</Code> with TLS and an App Password.
        </Step>
        <Step n={3} title="Set the folder">By default the <Code>INBOX</Code> folder is monitored. Change this to any IMAP folder path.</Step>
        <Step n={4} title="Save and test">Click <strong>Save</strong>. The Mail Service immediately performs a test connection. If it succeeds, monitoring starts.</Step>
      </Steps>
      <Screenshot alt="New mailbox form with host, port, email, and password fields" caption="Use App Passwords for Gmail and Outlook to avoid storing your main account password." />

      <H2 id="fields">Mailbox fields</H2>
      <PropTable props={[
        { name: 'host', type: 'string', required: true, description: 'IMAP server hostname, e.g. imap.gmail.com.' },
        { name: 'port', type: 'number', required: true, default: '993', description: 'IMAP port. 993 for TLS, 143 for STARTTLS.' },
        { name: 'tls', type: 'boolean', required: true, default: 'true', description: 'Enable TLS encryption.' },
        { name: 'email', type: 'string', required: true, description: 'The email address to monitor.' },
        { name: 'password', type: 'string', required: true, description: 'IMAP password or App Password.' },
        { name: 'folder', type: 'string', default: 'INBOX', description: 'IMAP folder to monitor.' },
      ]} />

      <H2 id="how-it-works">How it works</H2>
      <UL>
        <LI>The Mail Service polls each mailbox every few seconds for unseen messages</LI>
        <LI>Emails are stored with subject, from, to, body, and attachment metadata</LI>
        <LI>Attachments are forwarded to the Storage Service and linked to the email</LI>
        <LI>A <Code>email.received</Code> event is published to BUZZ with the email ID as payload</LI>
        <LI>Processed emails are marked as read so they are not processed again</LI>
      </UL>
      <Callout type="tip">Trigger a Brainer Preparation automatically when an email arrives by subscribing to <Code>email.received</Code> in BUZZ and calling the Brainer journey API from your webhook handler.</Callout>
    </Article>
  )
}
